import React from 'react';
import axios from 'axios';
import type { Client } from '../features/dashboard/types/dashboard';
import { useSelectedClient } from '../hooks/useSelectedClient';
import '../styles/selectclient.css';
import { useNavigate, useLocation } from 'react-router-dom';
import MomentumLogo from '../images/MomentumAMP Logo.png';

const FEATURES = [
  { icon: 'pi-file-edit', label: 'Certificates', desc: 'Issue and manage COIs instantly' },
  { icon: 'pi-send',      label: 'Requests',     desc: 'Track and fulfill holder requests' },
  { icon: 'pi-chart-bar', label: 'Reports',      desc: 'Insights across policies and clients' },
];

export type SelectClientPageProps = {
  onClientSelected?: (client: Client) => void;
};

type SearchResult = {
  id: string;
  name: string;
  typeName: string;
  type: number;
  policyNumber?: string | null;
  email?: string | null;
  phone?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  stateAbv?: string | null;
  zipCode?: string | null;
  policyEffectiveDate?: string | null;
  policyExpirationDate?: string | null;
};

function decodePolicyParam(raw: string): string {
  if (!raw) return raw;
  try {
    const decoded = atob(raw);
    if (/^[\x20-\x7E]+$/.test(decoded)) return decoded;
  } catch {}
  return raw;
}

function getInitials(name: string | undefined | null) {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

const TYPE_CONFIG: Record<string, {
  label: string;
  initBg: string;
  initColor: string;
  pillBg: string;
  pillColor: string;
  pillBorder: string;
}> = {
  Insured:  { label: 'Insured',  initBg: '#dcfce7', initColor: '#15803d', pillBg: '#dcfce7', pillColor: '#15803d', pillBorder: '#bbf7d0' },
  // Prospect: { label: 'Prospect', initBg: '#fef3c7', initColor: '#92400e', pillBg: '#fef3c7', pillColor: '#92400e', pillBorder: '#fde68a' },
  Policy:   { label: 'Policy',   initBg: '#f0ddff', initColor: '#8500D0', pillBg: '#f0ddff', pillColor: '#8500D0', pillBorder: '#e6b3ff' },
};

export default function SelectClientPage({ onClientSelected }: SelectClientPageProps) {
  const { setClient } = useSelectedClient();
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState<SearchResult[]>([]);
  const [selected, setSelected] = React.useState<SearchResult | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [continuing, setContinuing] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const [autoResolving, setAutoResolving] = React.useState(
    () => !!decodePolicyParam(new URLSearchParams(window.location.search).get('policy') ?? '')
  );
  const searchTimeout = React.useRef<number | null>(null);
  const stepTimers = React.useRef<number[]>([]);

  const autoPolicy = decodePolicyParam(new URLSearchParams(location.search).get('policy') ?? '');
  const STEPS = (selected?.typeName === 'Policy' || autoPolicy)
    ? ['Locating policy record...', 'Resolving insured customer...', 'Preparing dashboard...']
    : ['Verifying customer record...', 'Loading customer details...', 'Preparing dashboard...'];

  const startSteps = () => {
    stepTimers.current.forEach(t => window.clearTimeout(t));
    setActiveStep(0);
    stepTimers.current = [
      window.setTimeout(() => setActiveStep(1), 900),
      window.setTimeout(() => setActiveStep(2), 1800),
    ];
  };

  const stopSteps = () => {
    stepTimers.current.forEach(t => window.clearTimeout(t));
    setActiveStep(-1);
  };

  React.useEffect(() => {
    return () => { stepTimers.current.forEach(t => window.clearTimeout(t)); };
  }, []);

  React.useEffect(() => { setSelected(null); }, [query]);

  React.useEffect(() => {
    const autoPolicy = decodePolicyParam(new URLSearchParams(location.search).get('policy') ?? '');
    if (!autoPolicy) return;

    setContinuing(true);
    setError('');
    setActiveStep(0);
    const t1 = window.setTimeout(() => setActiveStep(1), 900);
    const t2 = window.setTimeout(() => setActiveStep(2), 1800);
    stepTimers.current = [t1, t2];

    const headers = {
      Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
      'Content-Type': 'application/json',
      NCToken: localStorage.getItem('NCToken') || '',
    };

    axios.get(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/insured-search`, {
      params: { searchText: autoPolicy },
      headers,
    })
      .then(async (res) => {
        const raw: any[] = Array.isArray(res.data) ? res.data : [];

        const policyResult = raw.find((c: any) => c.type === 700 &&
          (c.policyNumber || '').toLowerCase() === autoPolicy.toLowerCase())
          || raw.find((c: any) => c.type === 700);

        let client: Client | null = null;

        if (policyResult) {
          client = await resolveClientFromPolicy(policyResult);
        } else {
          const direct = raw.find((c: any) => c.type !== 700);
          if (direct?.id) client = await resolveClientFromId(direct.id, direct.name || autoPolicy);
        }

        if (client) {
          setClient(client);
          if (onClientSelected) onClientSelected(client);
          navigate('/dashboard', { replace: true });
        } else {
          setError(`No customer found for policy "${autoPolicy}". Please search manually.`);
          stepTimers.current.forEach(t => window.clearTimeout(t));
          setActiveStep(-1);
          setContinuing(false);
          setAutoResolving(false);
        }
      })
      .catch(() => {
        setError('Failed to load policy. Please search manually.');
        stepTimers.current.forEach(t => window.clearTimeout(t));
        setActiveStep(-1);
        setContinuing(false);
        setAutoResolving(false);
      });
  }, []);

  React.useEffect(() => {
    if (searchTimeout.current) window.clearTimeout(searchTimeout.current);

    if (!query.trim() || query.trim().length < 3) {
      setResults([]);
      setError('');
      return;
    }

    searchTimeout.current = window.setTimeout(() => {
      setLoading(true);
      setError('');
      axios.get(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/insured-search`, {
        params: { searchText: query },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
          'Content-Type': 'application/json',
          NCToken: localStorage.getItem('NCToken') || '',
        },
      })
        .then((res) => {
          const raw: any[] = Array.isArray(res.data) ? res.data : [];
          setResults(raw.map((item) => ({
            id: item.id,
            name: item.name || '',
            typeName: item.typeName || 'Insured',
            type: item.type ?? 0,
            policyNumber: item.policyNumber ?? null,
            email: item.email ?? null,
            phone: item.phone || item.cellPhone || null,
            addressLine1: item.addressLine1 ?? null,
            city: item.city ?? null,
            stateAbv: item.stateAbv ?? null,
            zipCode: item.zipCode ?? null,
            policyEffectiveDate: item.policyEffectiveDate ?? null,
            policyExpirationDate: item.policyExpirationDate ?? null,
          })));
        })
        .catch(() => { setResults([]); setError('Search failed. Please try again.'); })
        .finally(() => setLoading(false));
    }, 400);

    return () => { if (searchTimeout.current) window.clearTimeout(searchTimeout.current); };
  }, [query]);

  const resolveClientFromPolicy = async (policy: SearchResult): Promise<Client | null> => {
    const res = await axios.get(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/insured-by-policy-id`, {
      params: { policyId: policy.id },
      headers: { Authorization: `Bearer ${localStorage.getItem('igtoken')}`, 'Content-Type': 'application/json', NCToken: localStorage.getItem('NCToken') || '' },
    });
    const insuredDatabaseId: string | null = res.data?.insuredDatabaseId ?? null;
    if (!insuredDatabaseId) return null;
    return resolveClientFromId(insuredDatabaseId, policy.name);
  };

  const resolveClientFromId = async (databaseId: string, fallbackName: string): Promise<Client | null> => {
    const res = await axios.get(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/customer-by-id`, {
      params: { insuredDatabaseId: databaseId },
      headers: { Authorization: `Bearer ${localStorage.getItem('igtoken')}`, 'Content-Type': 'application/json', NCToken: localStorage.getItem('NCToken') || '' },
    });
    const detail = res.data?.value?.[0] ?? (Array.isArray(res.data) ? res.data[0] : res.data);
    if (!detail) return null;
    const stateVal = detail.stateNameOrAbbreviation || detail.state || '';
    const firstLast = [detail.firstName ?? '', detail.lastName ?? ''].map((p: string) => p?.trim()).filter(Boolean).join(' ');
    return {
      id: detail.databaseId || databaseId,
      name: detail.commercialName || fallbackName,
      typeLabel: 'Insured',
      primaryContactName: firstLast || fallbackName,
      addressLine1: detail.addressLine1 || '',
      addressLine2: detail.city && stateVal && detail.zipCode
        ? `${detail.city}, ${stateVal} ${detail.zipCode}` : undefined,
      city: detail.city || '',
      state: stateVal,
      zip: detail.zipCode || '',
      phone: detail.phone || detail.cellPhone || '',
      email: detail.eMail || detail.eMail2 || detail.eMail3 || '',
      primaryLocationDatabaseId: detail.primaryLocationDatabaseId ?? undefined,
    };
  };

  const handleContinue = async () => {
    if (!selected) return;
    setContinuing(true);
    setError('');
    startSteps();
    try {
      let client: Client | null = null;
      if (selected.typeName === 'Policy') {
        client = await resolveClientFromPolicy(selected);
        if (!client) {
          setError(`No customer found for policy "${selected.policyNumber || selected.name}". Try searching by customer name instead.`);
          setContinuing(false);
          stopSteps();
          return;
        }
      } else {
        client = await resolveClientFromId(selected.id, selected.name);
        if (!client) {
          setError('Customer details could not be loaded. Please try again.');
          setContinuing(false);
          stopSteps();
          return;
        }
      }
      setClient(client);
      if (onClientSelected) onClientSelected(client);
      setSelected(null);
      navigate('/dashboard');
    } catch {
      setError('Something went wrong. Please try again.');
      stopSteps();
    } finally {
      setContinuing(false);
    }
  };

  // ── Auto-policy loader ──
  if (autoResolving) {
    return (
      <div className="select-client-split-bg">
        {/* Left panel */}
        <div className="select-client-left">
          <div className="select-client-left-content">
            <img src={MomentumLogo} alt="Momentum AMP" className="select-client-logo-icon" />
            <div className="select-client-left-title">Certificate of Insurance Portal</div>
            <div className="select-client-left-desc">
              Automatically loading your policy and resolving the associated client account.
            </div>
            <div className="select-client-features">
              {FEATURES.map((f) => (
                <div key={f.icon} className="select-client-feature-row">
                  <div className="select-client-feature-icon"><i className={`pi ${f.icon}`} /></div>
                  <div>
                    <div className="select-client-feature-label">{f.label}</div>
                    <div className="select-client-feature-desc">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right panel — loader */}
        <div className="select-client-right">
          <div className="select-client-right-content" style={{ alignItems: 'center', justifyContent: 'center' }}>

            {/* Spinner ring */}
            <div style={{ position: 'relative', width: 72, height: 72, marginBottom: '2rem' }}>
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                border: '3px solid #e5e7eb',
                borderTop: '3px solid #A200FF',
                animation: 'select-client-spin 0.85s linear infinite',
              }} />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-file-check" style={{ fontSize: 22, color: '#A200FF' }} />
              </div>
            </div>

            {/* Title */}
            <div style={{ fontSize: '1.35rem', fontWeight: 700, color: '#0c0c0c', marginBottom: '0.35rem', letterSpacing: '-0.02em' }}>
              Loading your workspace
            </div>
            <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '2rem' }}>
              Policy&nbsp;<strong style={{ color: '#374151' }}>{autoPolicy}</strong>
            </div>

            {/* Sequential steps */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 360 }}>
              {STEPS.map((step, i) => {
                const done = i < activeStep;
                const active = i === activeStep;
                const pending = i > activeStep;
                return (
                  <div key={step} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 10,
                    background: active ? '#faf5ff' : done ? '#f0fdf4' : '#f9fafb',
                    border: `1.5px solid ${active ? '#e6b3ff' : done ? '#bbf7d0' : '#e5e7eb'}`,
                    transition: 'all 0.35s ease',
                  }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: active ? '#f0ddff' : done ? '#dcfce7' : '#f3f4f6',
                    }}>
                      {done    && <i className="pi pi-check"           style={{ fontSize: 12, color: '#16a34a' }} />}
                      {active  && <i className="pi pi-spin pi-spinner" style={{ fontSize: 12, color: '#A200FF' }} />}
                      {pending && <i className="pi pi-circle"          style={{ fontSize: 12, color: '#d1d5db' }} />}
                    </div>
                    <span style={{
                      fontSize: '0.9rem',
                      fontWeight: active ? 600 : done ? 500 : 400,
                      color: active ? '#381574' : done ? '#15803d' : '#9ca3af',
                    }}>
                      {step}
                    </span>
                    {done && (
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: '#16a34a', fontWeight: 500 }}>Done</span>
                    )}
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="select-client-split-bg">
      {/* Left panel */}
      <div className="select-client-left">
        <div className="select-client-left-content">
          <img src={MomentumLogo} alt="Momentum AMP" className="select-client-logo-icon" />
          <div className="select-client-left-title">Certificate of Insurance Portal</div>
          <div className="select-client-left-desc">
            Select a customer to access their policy and certificate of insurance information.
          </div>
          <div className="select-client-features">
            {FEATURES.map((f) => (
              <div key={f.icon} className="select-client-feature-row">
                <div className="select-client-feature-icon"><i className={`pi ${f.icon}`} /></div>
                <div>
                  <div className="select-client-feature-label">{f.label}</div>
                  <div className="select-client-feature-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="select-client-right">
        <div className="select-client-right-content">

          {/* Heading */}
          <div style={{ marginBottom: '1.2rem' }}>
            <div className="select-client-title">Select a customer or policy</div>
            <div className="select-client-subtitle">
              Search by name or policy number....
            </div>
          </div>

          <div className="select-client-search-card">

            <div className="select-client-searchbar">
              <div className="select-client-search-input-wrap">
                <i className="pi pi-search select-client-search-icon" />
                <input
                  className="select-client-search-input"
                  type="text"
                  placeholder="Search by name, policy number..."
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            {/* Type legend */}
            <div className="select-client-type-legend">
              <span className="select-client-type-legend-label">Types:</span>
              {Object.entries(TYPE_CONFIG).map(([key, conf]) => (
                <span key={key} className="select-client-type-pill" style={{ background: conf.pillBg, color: conf.pillColor, borderColor: conf.pillBorder }}>
                  {conf.label}
                </span>
              ))}
            </div>

            {/* Results list */}
            <div className="select-client-chip-list">

              {loading && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 10 }}>
                  <span className="select-client-spinner" />
                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Searching...</span>
                </div>
              )}

              {!loading && results.length === 0 && !query && (
                <div className="select-client-empty-state">
                  <svg className="select-client-empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <div className="select-client-empty-state-title">Start searching</div>
                  <div className="select-client-empty-state-message">
                    Enter a customer name or policy number to find a match
                  </div>
                </div>
              )}

              {!loading && query.trim().length > 0 && query.trim().length < 3 && (
                <div className="select-client-empty-state">
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    background: '#f0ddff', border: '1.5px solid #e6b3ff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A200FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                      <line x1="11" y1="8" x2="11" y2="11" /><line x1="11" y1="14" x2="11.01" y2="14" />
                    </svg>
                  </div>
                  <div className="select-client-empty-state-title" style={{ color: '#381574' }}>
                    Keep typing...
                  </div>
                  <div className="select-client-empty-state-message">
                    Enter at least <strong>3 characters</strong> to start searching
                  </div>
                </div>
              )}

              {!loading && results.length === 0 && query.trim().length >= 3 && (
                <div className="select-client-empty-state">
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    background: '#fffbeb', border: '1.5px solid #fde68a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="11" y1="8" x2="11" y2="11" />
                      <line x1="11" y1="14" x2="11.01" y2="14" />
                    </svg>
                  </div>
                  <div className="select-client-empty-state-title">No results found</div>
                  <div className="select-client-empty-state-message">
                    No match for <strong>"{query}"</strong><br />try a different name or policy number
                  </div>
                </div>
              )}

              {!loading && results.length > 0 && results.map((item) => {
                const conf = TYPE_CONFIG[item.typeName] ?? TYPE_CONFIG.Insured;
                const isSelected = selected?.id === item.id && selected?.typeName === item.typeName;
                return (
                  <div
                    key={`${item.typeName}-${item.id}`}
                    className={`select-client-chip${isSelected ? ' selected' : ''}`}
                    onClick={() => setSelected(item)}
                  >
                    <div className="select-client-chip-initials" style={{ background: conf.initBg, color: conf.initColor }}>
                      {getInitials(item.name)}
                    </div>
                    {/* Name + type badge */}
                    <div className="select-client-chip-info" style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
                        <span className="select-client-chip-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.typeName === 'Policy' ? (item.policyNumber || '—') : item.name}
                        </span>
                        <span style={{
                          flexShrink: 0,
                          fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                          padding: '1px 6px', borderRadius: 4,
                          background: conf.pillBg, color: conf.pillColor,
                          border: `1px solid ${conf.pillBorder}`,
                          lineHeight: '14px', textTransform: 'uppercase',
                        }}>
                          {conf.label}
                        </span>
                      </div>
                      <div className="select-client-chip-meta">
                        {item.typeName === 'Policy' ? item.name : (
                          item.city && item.stateAbv ? `${item.city}, ${item.stateAbv}` : item.phone || '—'
                        )}
                      </div>
                    </div>

                    {/* EFF / EXP dates — Policy only */}
                    {item.typeName === 'Policy' && (item.policyEffectiveDate || item.policyExpirationDate) && (
                      <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
                        gap: 4, flexShrink: 0, borderLeft: '1px solid #e5e7eb', paddingLeft: 12,
                      }}>
                        {item.policyEffectiveDate && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 9, fontWeight: 700, color: '#16a34a', background: '#dcfce7', borderRadius: 4, padding: '1px 6px', letterSpacing: 0.3, lineHeight: '14px' }}>EFF</span>
                            <span style={{ fontSize: 11, color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>
                              {new Date(item.policyEffectiveDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                            </span>
                          </span>
                        )}
                        {item.policyExpirationDate && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 9, fontWeight: 700, color: '#dc2626', background: '#fee2e2', borderRadius: 4, padding: '1px 6px', letterSpacing: 0.3, lineHeight: '14px' }}>EXP</span>
                            <span style={{ fontSize: 11, color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>
                              {new Date(item.policyExpirationDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                            </span>
                          </span>
                        )}
                      </div>
                    )}

                    <i className="pi pi-chevron-right" style={{ fontSize: 10, color: isSelected ? '#A200FF' : '#d1d5db', marginLeft: 8, flexShrink: 0 }} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#991b1b', fontSize: 13, margin: '0.6rem 0', padding: '8px 12px', background: '#fef2f2', borderRadius: 10, border: '1px solid #fecaca' }}>
              <i className="pi pi-exclamation-circle" style={{ fontSize: 14, flexShrink: 0 }} />
              {error}
            </div>
          )}

          {/* Footer */}
          <div className="select-client-footer-area">
            <button
              className={`select-client-continue-btn${continuing ? ' select-client-continue-btn--loading' : ''}`}
              disabled={!selected || continuing}
              onClick={handleContinue}
            >
              Continue to Dashboard <i className="pi pi-arrow-right" style={{ marginLeft: 8, fontSize: 14 }} />
            </button>

            {/* Step progress shown while loading */}
            {continuing && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
                {STEPS.map((step, i) => {
                  const done = i < activeStep;
                  const active = i === activeStep;
                  const pending = i > activeStep;
                  return (
                    <div key={step} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', borderRadius: 8,
                      background: active ? '#faf5ff' : done ? '#f0fdf4' : '#f9fafb',
                      border: `1px solid ${active ? '#e6b3ff' : done ? '#bbf7d0' : '#e5e7eb'}`,
                      transition: 'all 0.3s ease',
                    }}>
                      {done && <i className="pi pi-check-circle" style={{ fontSize: 14, color: '#16a34a', flexShrink: 0 }} />}
                      {active && <i className="pi pi-spin pi-spinner" style={{ fontSize: 13, color: '#A200FF', flexShrink: 0 }} />}
                      {pending && <i className="pi pi-circle" style={{ fontSize: 13, color: '#d1d5db', flexShrink: 0 }} />}
                      <span style={{
                        fontSize: 13, fontWeight: active ? 600 : 400,
                        color: active ? '#381574' : done ? '#15803d' : '#9ca3af',
                      }}>
                        {step}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* {!continuing && (
              <div className="select-client-footer-text">
                You can switch customers anytime from the header
              </div>
            )} */}

          </div>

        </div>
      </div>
    </div>
  );
}
